import type { PlaceValueResponse } from './place-value-api';

const API_BASE = '/api/v1/public/projects';
const VSJ_SLUG = 'valor-san-juan';

// ── Types ──────────────────────────────────────────────────────────────────────

export interface MapPlaceReading {
  streetName: string | null;
  streetTypeLabel: string | null;
  observationCount: number | null;
  observedTypes: string[] | null;
  periodLabel: string | null;
  /** GeoJSON geometry of the confirmed cuadra — used to illuminate it on the map. */
  confirmedGeometry: object | null;
}

export interface MapPlaceResult {
  lng: number;
  lat: number;
  valueResponse: PlaceValueResponse;
  /** Null when locate returned off_network or any call failed. */
  reading: MapPlaceReading | null;
}

// ── Raw shapes (mirrors vsj-locate-api / vsj-read-api without importing them) ─

interface LocateLocatedRaw {
  status: 'located';
  place: { streetName: string | null };
  placeToken: string;
  confirmedGeometry: object | null;
}

interface LocateRaw {
  status: string;
}

interface ReadRaw {
  streetTypeLabel: string;
  observationCount: number;
  observedTypes: string[];
  periodLabel: string;
}

// ── Private fetchers (all accept AbortSignal) ──────────────────────────────────

async function fetchNear(
  lng: number,
  lat: number,
  signal: AbortSignal
): Promise<PlaceValueResponse> {
  const params = new URLSearchParams({ lng: String(lng), lat: String(lat) });
  const res = await fetch(`${API_BASE}/${VSJ_SLUG}/places/near?${params.toString()}`, { signal });
  if (!res.ok) throw new Error(`near: HTTP ${res.status}`);
  return res.json() as Promise<PlaceValueResponse>;
}

async function fetchLocate(
  lng: number,
  lat: number,
  signal: AbortSignal
): Promise<LocateLocatedRaw | null> {
  try {
    const res = await fetch(`${API_BASE}/${VSJ_SLUG}/places/locate?lng=${lng}&lat=${lat}`, {
      signal,
    });
    if (!res.ok) return null;
    const data = (await res.json()) as LocateRaw;
    if (data.status !== 'located') return null;
    return data as LocateLocatedRaw;
  } catch (_e) {
    return null;
  }
}

async function fetchRead(token: string, signal: AbortSignal): Promise<ReadRaw | null> {
  try {
    const res = await fetch(
      `${API_BASE}/${VSJ_SLUG}/places/read?token=${encodeURIComponent(token)}`,
      { signal }
    );
    if (!res.ok) return null;
    return res.json() as Promise<ReadRaw>;
  } catch (_e) {
    return null;
  }
}

// ── Public orchestrator ────────────────────────────────────────────────────────

/**
 * Orchestrates near + locate → read for a single map click.
 * near and locate run in parallel. read runs only if locate succeeds.
 * locate/read errors degrade gracefully — reading is null, valueResponse still shown.
 * All calls respect the AbortSignal so stale requests from previous clicks are cancelled.
 */
export async function fetchMapPlace(
  lng: number,
  lat: number,
  signal: AbortSignal
): Promise<MapPlaceResult> {
  const [valueResponse, locateData] = await Promise.all([
    fetchNear(lng, lat, signal),
    fetchLocate(lng, lat, signal),
  ]);

  if (!locateData) {
    return { lng, lat, valueResponse, reading: null };
  }

  const readData = await fetchRead(locateData.placeToken, signal);

  return {
    lng,
    lat,
    valueResponse,
    reading: {
      streetName: locateData.place.streetName,
      streetTypeLabel: readData?.streetTypeLabel ?? null,
      observationCount: readData?.observationCount ?? null,
      observedTypes: readData?.observedTypes ?? null,
      periodLabel: readData?.periodLabel ?? null,
      confirmedGeometry: locateData.confirmedGeometry ?? null,
    },
  };
}
